import React, { useState, useEffect } from 'react';

const TestScoresAnalysis = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/test-scores`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching test scores data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const handleRefresh = () => {
      setLoading(true);
      fetchData();
    };
    window.addEventListener('analyticsRefresh', handleRefresh);
    return () => window.removeEventListener('analyticsRefresh', handleRefresh);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-xl shadow-lg animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4 w-1/4"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const ScoreCard = ({ title, scores, sections, testType, icon, colorScheme }) => {
    if (!scores || Object.keys(scores).length === 0) {
      return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-6">
            <div className={`p-3 rounded-lg ${colorScheme.bg} mr-4`}>
              <span className="text-2xl">{icon}</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">{title}</h3>
              <p className="text-sm text-gray-500">Standardized test scores</p>
            </div>
          </div>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-gray-500 text-lg">No {testType} data available</p>
            <p className="text-gray-400 text-sm mt-2">Students haven't submitted {testType} scores yet</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-center mb-6">
          <div className={`p-3 rounded-lg ${colorScheme.bg} mr-4`}>
            <span className="text-2xl">{icon}</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-500">Average scores by section</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section) => {
            const avgKey = `avg${section.key}`;
            const countKey = `count${section.key}`;
            const avgScore = scores[avgKey];
            const count = scores[countKey];

            // Debug logging

            if (!avgScore || !count || avgScore === 0) return null;

            const percentage = (avgScore / section.maxScore) * 100;
            const grade = getGradeLevel(percentage);

            return (
              <div key={section.key} className={`p-5 rounded-xl border-2 ${colorScheme.cardBorder} ${colorScheme.cardBg} hover:shadow-md transition-all duration-200`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-800 text-sm">{section.label}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${grade.bg} ${grade.text}`}>
                    {grade.label}
                  </span>
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline">
                    <span className={`text-3xl font-bold ${colorScheme.text}`}>
                      {avgScore.toFixed(1)}
                    </span>
                    <span className="text-lg text-gray-500 ml-1">
                      /{section.maxScore}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {percentage.toFixed(1)}% of maximum
                  </div>
                </div>

                <div className="mb-3">
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full ${colorScheme.progress} transition-all duration-500 ease-out`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">
                    <span className="font-medium">{count}</span> students
                  </span>
                  <span className={`font-medium ${colorScheme.text}`}>
                    Avg: {avgScore.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Overall Summary for this test */}
        <div className={`mt-6 p-4 rounded-lg ${colorScheme.summaryBg}`}>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className={`text-2xl font-bold ${colorScheme.text}`}>
                {Math.max(...sections.map(s => scores[`count${s.key}`] || 0))}
              </div>
              <div className="text-xs text-gray-600">Total Test Takers</div>
            </div>
            <div>
              <div className={`text-2xl font-bold ${colorScheme.text}`}>
                {sections.filter(s => scores[`avg${s.key}`] && scores[`avg${s.key}`] > 0).length}
              </div>
              <div className="text-xs text-gray-600">Sections Reported</div>
            </div>
            <div>
              <div className={`text-2xl font-bold ${colorScheme.text}`}>
                {calculateOverallAverage(scores, sections).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-600">Overall Performance</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getGradeLevel = (percentage) => {
    if (percentage >= 90) return { label: 'Excellent', bg: 'bg-green-100', text: 'text-green-800' };
    if (percentage >= 80) return { label: 'Good', bg: 'bg-blue-100', text: 'text-blue-800' };
    if (percentage >= 70) return { label: 'Fair', bg: 'bg-yellow-100', text: 'text-yellow-800' };
    if (percentage >= 60) return { label: 'Below Avg', bg: 'bg-orange-100', text: 'text-orange-800' };
    return { label: 'Needs Work', bg: 'bg-red-100', text: 'text-red-800' };
  };

  const calculateOverallAverage = (scores, sections) => {
    const validSections = sections.filter(s => scores[`avg${s.key}`] && scores[`avg${s.key}`] > 0);
    if (validSections.length === 0) return 0;
    
    const totalPercentage = validSections.reduce((sum, section) => {
      return sum + (scores[`avg${section.key}`] / section.maxScore * 100);
    }, 0);
    
    return totalPercentage / validSections.length;
  };

  // Define test sections with proper max scores
  const greeSections = [
    { key: 'Verbal', label: 'Verbal Reasoning', maxScore: 170 },
    { key: 'Quantitative', label: 'Quantitative Reasoning', maxScore: 170 },
    { key: 'Writing', label: 'Analytical Writing', maxScore: 6 }
  ];

  const toeflSections = [
    { key: 'Reading', label: 'Reading', maxScore: 30 },
    { key: 'Listening', label: 'Listening', maxScore: 30 },
    { key: 'Speaking', label: 'Speaking', maxScore: 30 },
    { key: 'Writing', label: 'Writing', maxScore: 30 }
  ];

  const ieltsSections = [
    { key: 'Overall', label: 'Overall Band', maxScore: 9 },
    { key: 'Reading', label: 'Reading', maxScore: 9 },
    { key: 'Listening', label: 'Listening', maxScore: 9 },
    { key: 'Speaking', label: 'Speaking', maxScore: 9 },
    { key: 'Writing', label: 'Writing', maxScore: 9 }
  ];

  // Color schemes for different tests
  const colorSchemes = {
    gre: {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      progress: 'bg-gradient-to-r from-blue-400 to-blue-600',
      cardBg: 'bg-blue-50',
      cardBorder: 'border-blue-200',
      summaryBg: 'bg-blue-50'
    },
    toefl: {
      bg: 'bg-green-100',
      text: 'text-green-600',
      progress: 'bg-gradient-to-r from-green-400 to-green-600',
      cardBg: 'bg-green-50',
      cardBorder: 'border-green-200',
      summaryBg: 'bg-green-50'
    },
    ielts: {
      bg: 'bg-purple-100',
      text: 'text-purple-600',
      progress: 'bg-gradient-to-r from-purple-400 to-purple-600',
      cardBg: 'bg-purple-50',
      cardBorder: 'border-purple-200',
      summaryBg: 'bg-purple-50'
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Test Scores Analysis</h2>
            <p className="text-gray-600">Comprehensive overview of standardized test performance</p>
          </div>
          <div className="text-6xl">📈</div>
        </div>
      </div>

      {/* GRE Scores */}
      <ScoreCard
        title="GRE Scores"
        scores={data?.gre}
        sections={greeSections}
        testType="GRE"
        icon="🎓"
        colorScheme={colorSchemes.gre}
      />

      {/* TOEFL Scores */}
      <ScoreCard
        title="TOEFL Scores"
        scores={data?.toefl}
        sections={toeflSections}
        testType="TOEFL"
        icon="🗣️"
        colorScheme={colorSchemes.toefl}
      />

      {/* IELTS Scores */}
      <ScoreCard
        title="IELTS Scores"
        scores={data?.ielts}
        sections={ieltsSections}
        testType="IELTS"
        icon="🌍"
        colorScheme={colorSchemes.ielts}
      />

      {/* Comparative Summary */}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="mr-3">📊</span>
          Test Score Comparison
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* GRE Summary */}
          {data?.gre && Object.keys(data.gre).length > 0 && (
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="text-4xl mb-3">🎓</div>
              <h4 className="font-bold text-blue-900 mb-2">GRE Test Takers</h4>
              <p className="text-4xl font-bold text-blue-600 mb-2">
                {Math.max(data.gre.countVerbal || 0, data.gre.countQuantitative || 0, data.gre.countWriting || 0)}
              </p>
              <p className="text-sm text-blue-700 mb-3">students</p>
              <div className="text-xs text-blue-600">
                Avg Performance: {calculateOverallAverage(data.gre, greeSections).toFixed(1)}%
              </div>
            </div>
          )}

          {/* TOEFL Summary */}
          {data?.toefl && Object.keys(data.toefl).length > 0 && (
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="text-4xl mb-3">🗣️</div>
              <h4 className="font-bold text-green-900 mb-2">TOEFL Test Takers</h4>
              <p className="text-4xl font-bold text-green-600 mb-2">
                {Math.max(data.toefl.countReading || 0, data.toefl.countListening || 0, data.toefl.countSpeaking || 0, data.toefl.countWriting || 0)}
              </p>
              <p className="text-sm text-green-700 mb-3">students</p>
              <div className="text-xs text-green-600">
                Avg Performance: {calculateOverallAverage(data.toefl, toeflSections).toFixed(1)}%
              </div>
            </div>
          )}

          {/* IELTS Summary */}
          {data?.ielts && Object.keys(data.ielts).length > 0 && (
            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
              <div className="text-4xl mb-3">🌍</div>
              <h4 className="font-bold text-purple-900 mb-2">IELTS Test Takers</h4>
              <p className="text-4xl font-bold text-purple-600 mb-2">
                {data.ielts.countOverall || 0}
              </p>
              <p className="text-sm text-purple-700 mb-3">students</p>
              <div className="text-xs text-purple-600">
                Avg Performance: {calculateOverallAverage(data.ielts, ieltsSections).toFixed(1)}%
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestScoresAnalysis;